import { useState } from 'react';
import { policyVersionPublish, policyVersions, type PolicyVersion } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { ErrorNote } from '../../ErrorNote';
import { Skeleton } from '../../ui/states';
import { RollbackDialog } from './dialogs';
import { actorWord, dayClock, pre, sx, useApi, type PolicyRole } from './util';

/** History (design pcIsHistory, T12853): every saved version, newest first, with rollback. */
export function HistoryTab({ pr, onGo }: { pr: PolicyRole; onGo: (t: 'Guards' | 'Playbooks') => void }) {
  const { client } = useSession();
  const { role } = useConsoleRole();
  const roReason = useReadOnlyReason();
  const isOp = pr === 'operator';
  const versions = useApi((c, signal) => policyVersions(c, { query: pr === 'facility' ? {} : { scope: 'network' }, signal }), [pr]);
  const [rollback, setRollback] = useState<{ id: string; label: string } | null>(null);
  const [publish, setPublish] = useState<PolicyVersion | null>(null);
  const vers = versions.data?.versions ?? [];
  return (
    <div data-testid="policy-history" style={sx('min-width:0;')}>
      <div style={sx('font-size:20px;font-weight:800;letter-spacing:-.018em;')}>Saved versions</div>
      {versions.loading && !versions.data && <div style={sx('margin-top:8px;')}><Skeleton rows={3} label="Loading versions" /></div>}
      {versions.error && !versions.data && <div style={sx('margin-top:8px;')}><ErrorNote error={versions.error} onRetry={versions.reload} /></div>}
      {vers.length > 0 && (
        <div style={sx('min-width:0;margin-top:8px;')}>
          {vers.map((v, i) => {
            const current = v.status === 'live';
            const canRoll = isOp && !roReason && !current && v.status === 'superseded';
            // Publishing applies the change: only Andrew (Super Admin) approves a policy change.
            const canPublish = role === 'SUPER_ADMIN' && !roReason && v.status === 'draft';
            const by = actorWord(pr, v.createdByName, v.createdByRole, null);
            return (
              <div key={v.id} style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;padding:9px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;animation:riseIn .28s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(240, i * 38) + 'ms;')}>
                <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{'v' + v.versionNo}</span>
                <span style={sx(current ? 'display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;color:#1F6B52;background:rgba(47,138,111,.12);white-space:nowrap;' : v.status === 'draft' || v.status === 'expired' ? 'display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;color:#3C454F;background:rgba(34,39,46,.13);white-space:nowrap;' : 'display:none;')}>
                  {current ? 'CURRENT' : v.status === 'draft' ? 'DRAFT' : v.status === 'expired' ? 'EXPIRED' : ''}
                </span>
                <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{dayClock(v.approvedAt ?? v.createdAt)}</span>
                <span style={sx('flex:1 1 90px;min-width:0;font-size:14px;color:#3C454F;')}>
                  {(v.summary ?? '') + (by ? ' · by ' + by : '') + (v.ledgerEntryId ? ' · sealed to the Truth Ledger' : '')}
                </span>
                {canRoll && (
                  <a href="#" onClick={(e) => { pre(e); setRollback({ id: v.id, label: 'v' + v.versionNo }); }} style={sx('display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;background:rgba(34,39,46,.05);color:#3C454F;border:1px solid rgba(34,39,46,.16);')}>
                    Roll back to this
                  </a>
                )}
                {canPublish && (
                  <a href="#" onClick={(e) => { pre(e); setPublish(v); }} style={sx('display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;background:#2F5C97;color:#fff;')}>
                    Review and publish
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* V9 empty state (design pcVerNone): what History keeps, and the first step. */}
      {versions.data && vers.length === 0 && (
        <div data-testid="policy-history-empty" style={sx('flex:1 1 100%;box-sizing:border-box;display:flex;flex-direction:column;gap:6px;margin-top:10px;padding:14px 16px;border-radius:10px;border:1px dashed rgba(34,39,46,.25);background:#FCFAF4;')}>
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>No changes saved yet</span>
          <span style={sx('font-size:16px;color:#3C454F;line-height:1.5;max-width:90ch;text-wrap:pretty;')}>
            History keeps every saved change to a guard, playbook or autonomy level, with exactly what changed and who saved it. Any version can be put back as a draft. First step: open Guards and tighten or loosen one rule, then save.
          </span>
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;margin-top:2px;')}>
            <a href="#" onClick={(e) => { pre(e); onGo('Guards'); }} style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#2F5C97;color:#fff;')}>
              Open Guards
            </a>
            <a href="#" onClick={(e) => { pre(e); onGo('Playbooks'); }} style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;border:1px solid rgba(34,39,46,.22);color:#22272E;')}>
              Open Playbooks
            </a>
          </div>
        </div>
      )}
      <div style={sx('font-size:14px;color:#54606C;margin-top:10px;padding-top:9px;border-top:1px solid rgba(34,39,46,.12);line-height:1.5;max-width:100ch;text-wrap:pretty;')}>
        Rolling back loads that version as a DRAFT: it does not apply until you save it, so a rollback is itself a reviewed, ledgered change. Versions are kept on the server with who saved them.
      </div>
      {rollback && <RollbackDialog id={rollback.id} label={rollback.label} onClose={() => setRollback(null)} onDone={() => versions.reload()} />}
      {publish && (
        <ConfirmDialog
          title={`Publish v${publish.versionNo}?`}
          confirmLabel="Publish"
          body={<p>{(publish.summary ?? 'This draft') + '. It goes live for ' + (publish.scope === 'network' ? 'every building' : 'this building') + ' and is sealed to the Truth Ledger.'}</p>}
          onConfirm={async () => {
            await policyVersionPublish(client, { params: { id: publish.id }, body: withConfirm({}) });
            versions.reload();
          }}
          onClose={() => setPublish(null)}
        />
      )}
    </div>
  );
}
